/**
 * Slideshow Password Lock
 * Handles password protection for slideshow slides
 */

class SlideshowPasswordLock {
  constructor() {
    this.init();
    this.bindSectionLoad();
  }

  init() {
    // Initialize all password locks on the page
    const passwordLocks = document.querySelectorAll('.slideshow-password-lock');
    
    passwordLocks.forEach(lockElement => {
      this.initPasswordLock(lockElement);
    });
  }

  /**
   * Initialize a single password lock
   */
  initPasswordLock(lockElement) {
    if (lockElement.dataset.passwordLockInitialized === 'true') {
      return;
    }

    const input = lockElement.querySelector('.password-input');
    const button = lockElement.querySelector('.password-submit-button');
    const errorMessage = lockElement.querySelector('.password-error-message');
    const correctPassword = lockElement.dataset.password;
    const redirectUrl = lockElement.dataset.redirectUrl;
    const cookieName = lockElement.dataset.cookieName || 'slideshow_unlocked';

    if (!input || !button) return;

    lockElement.dataset.passwordLockInitialized = 'true';

    // If already unlocked, keep the user on the current page and turn the control
    // into a direct CTA rather than redirecting automatically on page load.
    if (this.isUnlocked(cookieName)) {
      this.showUnlockedState(lockElement, redirectUrl);
      return;
    }

    // Handle button click
    button.addEventListener('click', () => {
      this.checkPassword(input, correctPassword, lockElement, redirectUrl, cookieName, errorMessage);
    });

    // Handle Enter key
    input.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        this.checkPassword(input, correctPassword, lockElement, redirectUrl, cookieName, errorMessage);
      }
    });
  }

  /**
   * Check if password is correct
   */
  checkPassword(input, correctPassword, lockElement, redirectUrl, cookieName, errorMessage) {
    const enteredPassword = input.value.trim();

    if (enteredPassword.toLowerCase() === correctPassword.trim().toLowerCase()) {
      // Password correct
      this.setUnlocked(cookieName);
      this.handleUnlock(lockElement, redirectUrl);
      
      // Hide error message
      if (errorMessage) {
        errorMessage.style.display = 'none';
      }
    } else {
      // Password incorrect
      this.showError(errorMessage, input);
    }
  }

  /**
   * Handle successful unlock
   */
  handleUnlock(lockElement, redirectUrl) {
    // Dispatch custom event
    lockElement.dispatchEvent(new CustomEvent('password-unlocked', {
      bubbles: true,
      detail: { lockElement, redirectUrl }
    }));

    if (redirectUrl) {
      // Redirect to specified URL
      window.location.href = redirectUrl;
    } else {
      this.showUnlockedState(lockElement, redirectUrl);
    }
  }

  showUnlockedState(lockElement, redirectUrl) {
    const input = lockElement.querySelector('.password-input');
    const button = lockElement.querySelector('.password-submit-button');
    const errorMessage = lockElement.querySelector('.password-error-message');

    if (errorMessage) {
      errorMessage.style.display = 'none';
    }

    if (input) {
      input.value = '';
      input.disabled = true;
      input.placeholder = 'Access granted';
      input.setAttribute('aria-disabled', 'true');
    }

    if (button) {
      button.textContent = redirectUrl ? 'Continue' : 'Unlocked';
      button.disabled = false;

      if (redirectUrl) {
        button.addEventListener('click', () => {
          window.location.href = redirectUrl;
        }, { once: true });
      } else {
        button.disabled = true;
      }
    }

    if (!lockElement.querySelector('.password-success')) {
      const successMessage = document.createElement('div');
      successMessage.className = 'password-success';
      successMessage.textContent = redirectUrl ? 'Access granted. Continue to enter.' : 'Unlocked!';
      lockElement.appendChild(successMessage);
    }
  }

  /**
   * Show error message
   */
  showError(errorMessage, input) {
    if (errorMessage) {
      errorMessage.style.display = 'block';
      
      // Shake animation
      input.classList.add('password-input--error');
      setTimeout(() => {
        input.classList.remove('password-input--error');
      }, 500);

      // Hide error after 3 seconds
      setTimeout(() => {
        errorMessage.style.display = 'none';
      }, 3000);
    }

    // Clear input
    input.value = '';
    input.focus();
  }

  /**
   * Check if unlocked via cookie
   */
  isUnlocked(cookieName) {
    return this.getCookie(cookieName) === 'true';
  }

  /**
   * Set unlocked cookie
   */
  setUnlocked(cookieName) {
    // Set cookie for 30 days
    const days = 30;
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    const expires = "expires=" + date.toUTCString();
    document.cookie = cookieName + "=true;" + expires + ";path=/;SameSite=Lax";
  }

  /**
   * Get cookie value
   */
  getCookie(name) {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  }

  /**
   * Clear unlock cookie (for testing)
   */
  clearUnlock(cookieName) {
    document.cookie = cookieName + "=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
  }

  bindSectionLoad() {
    document.addEventListener('shopify:section:load', (event) => {
      if (!event.target) {
        return;
      }

      event.target.querySelectorAll('.slideshow-password-lock').forEach((lockElement) => {
        this.initPasswordLock(lockElement);
      });
    });
  }
}

// Initialize password locks
document.addEventListener('DOMContentLoaded', () => {
  window.slideshowPasswordLock = new SlideshowPasswordLock();
});

// Also initialize immediately if DOMContentLoaded already fired
if (document.readyState === 'interactive' || document.readyState === 'complete') {
  window.slideshowPasswordLock = new SlideshowPasswordLock();
}

// Debug function (only when ?password_debug=true)
if (window.location.search.includes('password_debug=true')) {
  window.passwordLockDebug = {
    clearCookie: (cookieName) => {
      if (window.slideshowPasswordLock) {
        window.slideshowPasswordLock.clearUnlock(cookieName);
        console.log(`Cleared cookie: ${cookieName}`);
      }
    },
    checkCookie: (cookieName) => {
      if (window.slideshowPasswordLock) {
        const isUnlocked = window.slideshowPasswordLock.isUnlocked(cookieName);
        console.log(`Cookie ${cookieName} unlocked:`, isUnlocked);
        return isUnlocked;
      }
    }
  };
  console.log('Password Lock Debug Mode Active. Use window.passwordLockDebug');
}
